import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import * as styles from './styles';
import { AddressInputProps } from './models';
import AnimatedInput from '../AnimatedInput/AnimatedInput';
import { getAddressFromGooglePlaceString, getGoogleStringFromAddress } from '@sunbit-dev/common/helpers/address';
import { withTheme } from '../themeProvider/ThemeProvider';

export const AddressInput = withTheme(styled(
  ({
     className,
     onChange: _onChange,
     options,
     value,
     onBlur: _onBlur,
     inputRef: parentInputRef,
     ...restProps
   }: AddressInputProps) => {
    const autoCompleteRef = useRef<google.maps.places.Autocomplete>();
    const inputRef = parentInputRef || useRef<HTMLInputElement>();
    const [searchText, setSearchText] = useState();

    const onChangeRef = useRef(_onChange);
    onChangeRef.current = _onChange;

    useEffect(() => {
      if (!window.google.maps.places.Autocomplete) {
        console.log('Make sure to load google places api script');
        return;
      }

      autoCompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, options);

      autoCompleteRef.current.addListener('place_changed', async () => {
        const place = await autoCompleteRef.current.getPlace();
        const address = getAddressFromGooglePlaceString({ place });
        setSearchText(undefined);
        onChangeRef.current({ value: address });
      });
    }, []);

    const onBlur = useCallback(
      (e) => {
        setSearchText(undefined);
        _onBlur(e);
      },
      [_onBlur]
    );

    const onChange = useCallback((event) => {
      setSearchText(event.target.value);
    }, []);

    const formattedValue = useMemo(() => {
      const { address, city, state, zipcode } = value || {};
      return searchText ?? getGoogleStringFromAddress({ address, city, state, zipcode });
    }, [value, searchText]);

    return (
      <>
        <styles.GlobalStyle/>
        <AnimatedInput
          {...{ className, inputRef, onBlur, onChange, value: formattedValue, placeholder: '', ...restProps }}
        />
      </>
    );
  }
)`
  ${styles.AddressInput}
`);
